import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
} from "@ionic/react";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Polyline,
  Marker,
} from "@react-google-maps/api";
import { closeOutline } from "ionicons/icons";
import { environment } from "../environment/environment";
import { LatLng } from "../utils/jeepneyPlanner";
import "../pages/MapModal.css";
import "./ChatRouteModal.css";

// A simple origin → destination preview for regional/intercity trips answered
// by askTransport (no local jeepney geometry available).
export interface ChatTransportRoutePreview {
  originLabel: string;
  destinationLabel: string;
  origin: LatLng;
  destination: LatLng;
}

interface Props {
  isOpen: boolean;
  route: ChatTransportRoutePreview | null;
  onClose: () => void;
}

const libraries: ("places")[] = ["places"];

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: "greedy" as const,
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
};

const TransportRouteModal: React.FC<Props> = ({ isOpen, route, onClose }) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [useStraightLine, setUseStraightLine] = useState(false);
  const [isPresented, setIsPresented] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapRenderKey, setMapRenderKey] = useState(0);
  const recoveryAttemptedRef = useRef(false);
  const recoveryTimerRef = useRef<number | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: environment.GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapCenter = useMemo(() => route?.origin ?? { lat: 10.6765, lng: 122.9509 }, [route]);

  useEffect(() => {
    if (!isOpen) {
      recoveryAttemptedRef.current = false;
      setIsMapReady(false);
      setMapRenderKey(0);
    }
  }, [isOpen, route]);

  // Try a driving route; fall back to a straight line when there's no road path
  // (e.g. a sea crossing to an island like Lakawon).
  useEffect(() => {
    if (!route || !isOpen || !isLoaded) {
      setDirections(null);
      setUseStraightLine(false);
      return;
    }
    const service = new google.maps.DirectionsService();
    service.route(
      {
        origin: route.origin,
        destination: route.destination,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);
          setUseStraightLine(false);
        } else {
          setDirections(null);
          setUseStraightLine(true);
        }
      }
    );
  }, [isLoaded, isOpen, route]);

  const fitBounds = (instance: google.maps.Map) => {
    if (!route) return;
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(route.origin);
    bounds.extend(route.destination);
    directions?.routes?.[0]?.overview_path?.forEach((p) => bounds.extend(p));
    instance.fitBounds(bounds, 64);
  };

  useEffect(() => {
    if (!map || !route || !isPresented) return;
    const firstPass = window.setTimeout(() => {
      window.google?.maps?.event?.trigger(map, "resize");
      fitBounds(map);
    }, 120);
    const secondPass = window.setTimeout(() => {
      window.google?.maps?.event?.trigger(map, "resize");
      fitBounds(map);
    }, 420);
    return () => {
      window.clearTimeout(firstPass);
      window.clearTimeout(secondPass);
    };
    // eslint-disable-next-line
  }, [isPresented, map, route, directions]);

  useEffect(() => () => {
    if (recoveryTimerRef.current) {
      window.clearTimeout(recoveryTimerRef.current);
    }
  }, []);

  if (!route) return null;

  return (
    <IonModal
      isOpen={isOpen}
      keepContentsMounted={true}
      onDidDismiss={onClose}
      onDidPresent={() => setIsPresented(true)}
      onWillDismiss={() => {
        setIsPresented(false);
        setIsMapReady(false);
        setMap(null);
      }}
      className="chat-route-modal"
    >
      <IonHeader className="map-modal-header">
        <IonToolbar>
          <IonTitle>Route Map</IonTitle>
          <IonButton className="map-header-close-button" fill="clear" slot="end" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent className="chat-route-content">
        {loadError ? (
          <div className="chat-route-status">Failed to load Google Maps.</div>
        ) : !isLoaded ? (
          <div className="chat-route-status">Loading route map...</div>
        ) : (
          <div className="chat-route-shell">
            <div className="chat-route-map">
              {isPresented && (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={11}
                  key={`${route.originLabel}-${route.destinationLabel}-${mapRenderKey}`}
                  onLoad={(instance) => {
                    setMap(instance);
                    setIsMapReady(false);
                    if (recoveryTimerRef.current) {
                      window.clearTimeout(recoveryTimerRef.current);
                    }
                    let ticks = 0;
                    const id = window.setInterval(() => {
                      window.dispatchEvent(new Event("resize"));
                      if (++ticks >= 12) window.clearInterval(id);
                    }, 600);
                    window.google?.maps?.event?.addListenerOnce(instance, "tilesloaded", () => {
                      window.clearInterval(id);
                      if (recoveryTimerRef.current) {
                        window.clearTimeout(recoveryTimerRef.current);
                        recoveryTimerRef.current = null;
                      }
                      setIsMapReady(true);
                      fitBounds(instance);
                    });
                    recoveryTimerRef.current = window.setTimeout(() => {
                      const paintedTileCount = instance
                        .getDiv()
                        ?.querySelectorAll("img, canvas").length ?? 0;

                      if (paintedTileCount === 0 && !recoveryAttemptedRef.current) {
                        recoveryAttemptedRef.current = true;
                        setIsMapReady(false);
                        setMap(null);
                        setMapRenderKey((current) => current + 1);
                        return;
                      }

                      setIsMapReady(true);
                      fitBounds(instance);
                    }, 1800);
                  }}
                  options={mapOptions}
                >
                  {isMapReady && directions && !useStraightLine && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{
                        polylineOptions: { strokeColor: "#43ae91", strokeWeight: 5 },
                        suppressMarkers: true,
                        preserveViewport: true,
                      }}
                    />
                  )}

                  {isMapReady && useStraightLine && (
                    <Polyline
                      path={[route.origin, route.destination]}
                      options={{
                        strokeColor: "#43ae91",
                        strokeOpacity: 0,
                        icons: [
                          {
                            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeWeight: 3, scale: 3 },
                            offset: "0",
                            repeat: "14px",
                          },
                        ],
                      }}
                    />
                  )}

                  {isMapReady && (
                    <Marker
                      position={route.origin}
                      label={{ text: "A", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                  {isMapReady && (
                    <Marker
                      position={route.destination}
                      label={{ text: "B", fontSize: "14px", fontWeight: "bold", color: "#ffffff" }}
                    />
                  )}
                </GoogleMap>
              )}
            </div>

            <div className="chat-route-panel">
              <div className="chat-route-header-card">
                <span className="chat-route-eyebrow">Trip route</span>
                <h3>
                  {route.originLabel} to {route.destinationLabel}
                </h3>
                <p>
                  {useStraightLine
                    ? "Approximate direction shown — this trip likely needs a bus/van and possibly a boat, so there isn't a single road route."
                    : "Suggested road route between your origin and destination. See the chat for fares and terminals."}
                </p>
              </div>
            </div>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default TransportRouteModal;
